/* eslint-disable no-param-reassign */
import { PrstTransform } from '../../prst-transform/prst-transform';
import { RenderStateStruct } from '../../type/common.interface';
import { detectChar } from './detect-char';
import { detectRect } from './detect-rect';
import { generateBounds } from './generate-bounds';
import { generateCmds } from './generate-cmds';
import { layoutNode } from './layout-node';
import { transformNode } from './transform-node';

export function schedule(renderStateStruct: RenderStateStruct) {
    const { prstTxWarp } = renderStateStruct;
    const { charStructList = [], bgStructList = [], ulStructList = [], ltStructList = [] } = renderStateStruct;

    const charLayoutRes = [] as any[];
    const bgLayoutRes = [] as any[];
    const ulLayoutRes = [] as any[];
    const ltLayoutRes = [] as any[];

    const contentArea = {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity,
        width: 0,
        height: 0,
        offset: { x: 0, y: 0 },
    };

    // char 排版
    renderStateStruct.charStructList.forEach((req: any) => {
        const { id, char, font, width, height, offset } = req;
        // char 检测
        const { charDirectedCycles } = detectChar(char, font, width, height);

        const layoutedCycles = layoutNode({
            pointsList: charDirectedCycles,
            offset,
            contentArea,
        });

        const boundsPoints = [
            { x: 0, y: 0 },
            { x: width, y: 0 },
            { x: width, y: height },
            { x: 0, y: height },
        ];

        boundsPoints.forEach((point) => {
            point.x += offset.x;
            point.y += offset.y;
        });

        const centerPoint = {
            x: width / 2,
            y: height / 2,
        };

        centerPoint.x += offset.x;
        centerPoint.y += offset.y;

        charLayoutRes.push({ id, layoutedCycles, boundsPoints, centerPoint });
    });

    // bg 排版
    renderStateStruct.bgStructList.forEach((req: any) => {
        const { id, width, height, offset } = req;
        // bg 检测
        const { rectDirectedCycle } = detectRect(width, height);
        const layoutedCycles = layoutNode({
            pointsList: [rectDirectedCycle],
            offset,
            contentArea,
        });
        bgLayoutRes.push({ id, layoutedCycles });
    });

    // underline 排版
    renderStateStruct.ulStructList.forEach((req: any) => {
        const { id, width, height, offset } = req;
        // ul 检测
        const { rectDirectedCycle } = detectRect(width, height);
        const layoutedCycles = layoutNode({
            pointsList: [rectDirectedCycle],
            offset,
            contentArea,
        });
        ulLayoutRes.push({ id, layoutedCycles });
    });

    // lineThrough 排版
    renderStateStruct.ltStructList.forEach((req: any) => {
        const { id, width, height, offset } = req;
        // lt 检测
        const { rectDirectedCycle } = detectRect(width, height);
        const layoutedCycles = layoutNode({
            pointsList: [rectDirectedCycle],
            offset,
            contentArea,
        });
        ltLayoutRes.push({ id, layoutedCycles });
    });

    // 调整 contentArea, 防止变形边缘粘黏
    contentArea.minX -= 5;
    contentArea.minY -= 5;
    contentArea.maxX += 5;
    contentArea.maxY += 5;

    // 计算 contentArea 宽高和偏移量
    contentArea.width = contentArea.maxX - contentArea.minX;
    contentArea.height = contentArea.maxY - contentArea.minY;
    contentArea.offset.x = contentArea.minX;
    contentArea.offset.y = contentArea.minY;

    // 设置变形线段的点
    PrstTransform.getInstance().setTransformLinesPoints(prstTxWarp, contentArea);

    // 执行任务
    charLayoutRes.forEach(({ layoutedCycles, boundsPoints, centerPoint }, index) => {
        const transformedCycles = transformNode(prstTxWarp, contentArea, layoutedCycles);
        const [transformedBoundsPoints] = transformNode(prstTxWarp, contentArea, [boundsPoints]);

        const cmds = generateCmds(transformedCycles);
        const bounds = generateBounds(transformedBoundsPoints);
        const [[center]] = transformNode(prstTxWarp, contentArea, [[centerPoint]]);

        Object.assign(charStructList[index], { cmds, bounds, center });
    });

    [bgLayoutRes, ulLayoutRes, ltLayoutRes].forEach((layoutRes, index1) => {
        layoutRes.forEach(({ layoutedCycles }, index2) => {
            const transformedCycles = transformNode(prstTxWarp, contentArea, layoutedCycles);
            const cmds = generateCmds(transformedCycles);
            Object.assign([bgStructList, ulStructList, ltStructList][index1][index2], { cmds });
        });
    });

    // 轻量的文字信息列表
    renderStateStruct.charStructListLite = renderStateStruct.charStructList.map(({ id, bounds, center }) => ({
        id,
        bounds,
        center,
    }));

    renderStateStruct.contentArea = contentArea;
}
